import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useAccessToken } from '@/services/auth';
import {
  type OnFarmSourceTotals,
  onFarmTotalsOptions,
} from '@/services/on-farm-implant';

import type { EmbryoSource, LoadedSource } from '../types';

export const SOURCE_METADATA: Record<
  string,
  { title: string; icon: string; description: string }
> = {
  fresh: {
    title: 'Fresh Embryos',
    icon: '💧',
    description: 'Fresh embryos available for implant preparation.',
  },
  frozen: {
    title: 'Frozen Embryos',
    icon: '❄️',
    description: 'Frozen embryos available from active inventory.',
  },
  flush: {
    title: 'Flush Embryos',
    icon: '🌊',
    description: 'Embryos coming from the Flush App.',
  },
  'client-on-farm-tog': {
    title: 'Client On-Farm TOG Embryos',
    icon: '📦',
    description: "TransOva-created embryos held on the client's farm.",
  },
  'non-tog': {
    title: 'Non-TOG Embryos',
    icon: '⊕',
    description: 'Add embryos not produced by TransOva.',
  },
};

const QUERIED_SOURCES = [
  { displayId: 'fresh', totalsKey: 'fresh' },
  { displayId: 'frozen', totalsKey: 'frozen' },
  { displayId: 'flush', totalsKey: 'flush' },
  { displayId: 'client-on-farm-tog', totalsKey: 'clientOnFarm' },
] as const;

export function buildLoadedSource(
  sourceId: string,
  totals: OnFarmSourceTotals | undefined,
): LoadedSource {
  const meta = SOURCE_METADATA[sourceId];
  return {
    id: `loaded-${sourceId}`,
    sourceId,
    title: meta?.title ?? sourceId,
    icon: meta?.icon ?? '🧫',
    items: [],
    hasMore: false,
    totalRecords: totals?.totalRecords ?? 0,
    totalEmbryos: totals?.totalEmbryos ?? 0,
  };
}

type UseOnFarmImplantSourcesResult = {
  embryoSources: EmbryoSource[];
  loadedSources: LoadedSource[];
  isLoading: boolean;
  isError: boolean;
};

export function useOnFarmImplantSources(
  siteId: string | null,
  accountId?: string,
): UseOnFarmImplantSourcesResult {
  const token = useAccessToken();
  const enabled = !!siteId;

  const {
    data: totals,
    isLoading,
    isError,
  } = useQuery({
    ...onFarmTotalsOptions(token, { siteId: siteId ?? '', accountId }),
    enabled: !!token && enabled,
  });

  const embryoSources = useMemo<EmbryoSource[]>(
    () => [
      ...QUERIED_SOURCES.map(({ displayId, totalsKey }) => {
        const count = totals?.[totalsKey]?.totalRecords ?? null;
        return {
          id: displayId,
          type: displayId as EmbryoSource['type'],
          ...SOURCE_METADATA[displayId],
          availableCount:
            displayId === 'client-on-farm-tog' && count === 0 ? null : count,
        };
      }),
      {
        id: 'non-tog',
        type: 'non-tog' as const,
        ...SOURCE_METADATA['non-tog'],
        availableCount: null,
        manualEntry: true,
      },
    ],
    [totals],
  );

  const loadedSources = useMemo<LoadedSource[]>(
    () =>
      QUERIED_SOURCES.map(({ displayId, totalsKey }) =>
        buildLoadedSource(displayId, totals?.[totalsKey]),
      ),
    [totals],
  );

  return { embryoSources, loadedSources, isLoading, isError };
}
